import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Disclaimer } from "../components/Disclaimer";
import { SiteBackground } from "../components/SiteBackground";
import { Compass, Check, KeyRound, CalendarClock, CalendarDays, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Paywall() {
  const { refresh, logout } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => { api.get("/billing/config").then((r) => setConfig(r.data)); }, []);

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await api.post("/billing/redeem", { code });
      await refresh();
      toast.success("Access unlocked. Welcome in!");
      nav("/home");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRedeeming(false); }
  };

  const checkout = async (lookup_key) => {
    setLoadingPlan(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setLoadingPlan(null); }
  };

  const PlanCard = ({ testid, icon: Icon, title, price, per, lookup, features, note }) => (
    <div className="pm-card p-6" data-testid={testid}>
      <div className="flex items-center gap-2"><Icon className="w-5 h-5" /><span className="font-head font-bold text-lg">{title}</span></div>
      <div className="mt-2 font-mono text-3xl font-bold">${price}<span className="text-base font-normal">{per}</span></div>
      <div className="label-mono">{note}</div>
      <ul className="mt-4 space-y-1.5 text-sm">
        {features.map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: "var(--earn)" }} /> {f}</li>)}
      </ul>
      <button onClick={() => checkout(lookup)} disabled={!!loadingPlan} className="pm-btn w-full mt-5" data-testid={`subscribe-${testid}`}>
        {loadingPlan === lookup ? "Redirecting…" : "Subscribe"}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen relative">
      <SiteBackground variant="brand" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2"><Compass className="w-6 h-6" /><span className="font-head font-extrabold text-lg">Path Mentor</span></div>
          <button onClick={async () => { await logout(); nav("/"); }} className="pm-btn-ghost text-sm flex items-center gap-1.5" data-testid="paywall-logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>

        <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight">Unlock Path Mentor</h1>
        <p className="text-sm mt-2 mb-8" style={{ color: "var(--text-2)" }}>
          Your AI mentor, local scans, measurements, invoices, and saved money paths. Pick a plan — or enter an access code.
        </p>

        <div className="pm-card p-5 mb-6" data-testid="access-code-card">
          <div className="flex items-center gap-2 label-mono mb-3"><KeyRound className="w-4 h-4" /> Access code</div>
          <div className="flex gap-2">
            <input className="pm-input font-mono" placeholder="Enter access code" value={code}
              onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && redeem()} data-testid="access-code-input" />
            <button onClick={redeem} disabled={redeeming} className="pm-btn whitespace-nowrap" data-testid="redeem-btn">
              {redeeming ? "…" : "Redeem"}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <PlanCard testid="monthly" icon={CalendarClock} title="Monthly" price={config?.monthlyAmount || "49.00"} per="/mo"
            lookup="pm_monthly" note="billed monthly · cancel anytime"
            features={["Full mentor chat", "Scan & measure", "Local license links", "Invoices & saved paths"]} />
          <PlanCard testid="yearly" icon={CalendarDays} title="Yearly" price={config?.yearlyAmount || "490.00"} per="/yr"
            lookup="pm_yearly" note="billed yearly · save ~2 months"
            features={["Everything in Monthly", "Save ~2 months vs monthly", "Best value"]} />
        </div>

        <div className="mt-8"><Disclaimer compact /></div>
      </div>
    </div>
  );
}
